import { gql, useApolloClient } from "@apollo/client";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ButtonCreate } from "../../../../commons/button/Button.js";
import { useUrlSearchParams } from "../../../../commons/hooks/useUrlSearchParams.js";
import { QUOTE_TEMPLATE_ITEM_TYPES } from "../../../../constants.js";
import { buildDocPath } from "../../../../utils.js";
import {
  CreateQuoteTemplateModal,
  FormValues,
} from "../../CreateQuoteTemplateModal.js";
import {
  useCreateQuoteTemplateButton_SetItemProductMutation,
  useCreateQuoteTemplateMutation,
} from "./CreateTemplateQuoteButton.generated.js";
import { CREATE_TEMPLATE_URL_PARAM } from "./createTemplateUrlParam.js";

interface Props {
  pathToDocList: string;
  templateTypeId?: string;
}

export const CreateQuoteTemplateButton = ({
  pathToDocList,
  templateTypeId,
}: Props) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate("Templates");

  // ?createTemplate (a bare presence flag, matching
  // ProjectOverviewHeaderBox.tsx's ?create) names the modal's own open
  // state — kept in sync in both directions, not just as a one-shot
  // auto-open trigger: opening the modal via the button also sets the
  // param, and closing it (by either path) clears it. That's why open
  // state is managed locally here rather than via ModalOpenButton, which
  // has no way to observe or drive an external open/close signal. Staying
  // in the URL while open means a refresh reopens it, rather than silently
  // dropping progress on a bare page.
  const { urlSearchParams, setUrlSearchParams } = useUrlSearchParams();
  const [isOpen, setIsOpen] = React.useState(() =>
    urlSearchParams.has(CREATE_TEMPLATE_URL_PARAM)
  );
  const openModal = () => {
    setIsOpen(true);
    setUrlSearchParams({ [CREATE_TEMPLATE_URL_PARAM]: "" }, undefined, {
      replace: true,
    });
  };
  const closeModal = () => {
    setIsOpen(false);
    setUrlSearchParams({}, [CREATE_TEMPLATE_URL_PARAM], { replace: true });
  };

  const client = useApolloClient();

  const [createQuoteTemplate] = useCreateQuoteTemplateMutation({ client });
  const [setItemProduct] = useCreateQuoteTemplateButton_SetItemProductMutation({
    client,
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const response = await createQuoteTemplate({
        variables: {
          input: {
            title: values.title,
            description: values.description,
            discountPercentage: 0,
            applyTemplate: true,
            rootItemType: "section",
            templateTypeId: values.templateType?.id,
            category: values.category,
            typeOfTemplate: values.typeOfTemplate,
            typeOfProject: values.typeOfProject,
            teamId: values.teamId,
          },
        },
      });
      const template = response.data?.createQuoteTemplate.quoteTemplate;
      const docId = template?.id;

      if (!docId) throw new Error("Document id missing");

      const rootItemId = template?.rootItem.id;
      const rootItemType = template?.rootItem.type;

      if (!rootItemId || !rootItemType) throw new Error("Root item id missing");

      if (rootItemType === "paid" && values.product) {
        await setItemProduct({
          variables: {
            input: {
              projectId: null,
              docId,
              itemId: rootItemId,
              productArticleNumber: values.product.articleNumber,
              productSupplierId: values.product.supplierId,
              expandedItemIds: [],
            },
          },
        });
      }

      navigate(
        buildDocPath(
          `${pathToDocList}/${docId}`,
          template.resolvedAsReadModelVersionNumber ?? null
        )
      );
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  return (
    <>
      <ButtonCreate
        type="button"
        color="primary"
        variant="contained"
        title={t("New Template")}
        onClick={openModal}
      />
      {isOpen && (
        <CreateQuoteTemplateModal
          handleComplete={handleSubmit}
          initialTemplateTypeId={templateTypeId}
          handleClose={closeModal}
        />
      )}
    </>
  );
};
